import FeaturesMap from '@apps-common/ui-maps/FeaturesMap.jsx';
import GeojsonLayer from '@apps-common/ui-maps/GeojsonLayer.jsx';
import React, {useEffect, useMemo} from 'react';
import {Box, IconButton, Typography} from '@mui/material';
import {
  ArrowBack as LeftArrow,
  ArrowForward as RightArrow,
} from '@mui/icons-material';
import {createCandidates} from '@apps-common/geocorn-sdk/geom/simplify.js';
import {func, number, object} from 'prop-types';
import {getArea} from '@apps-common/geocorn-sdk/geom/cea.js';
import {useTheme} from '@mui/material/styles/index.js';

import {CandidateChooser} from './CandidateChooser.jsx';
import {MapPanel, Sidebar} from '../Common.jsx';

const FeatureCard = ({
  feature,
  selectedGeometry,
  onChange,
  currentFeature,
  setCurrentFeature,
  nFeatures,
  maxVertexes = 1500,
}) => {
  const originalArea = getArea(feature.geometry);
  const candidates = useMemo(
    () => createCandidates(feature.geometry, {maxVertexCount: maxVertexes}),
    [feature.geometry, maxVertexes],
  );
  const selectedFeatures = selectedGeometry
    ? [{type: 'Feature', geometry: selectedGeometry.geometry}]
    : [];
  const theme = useTheme();

  useEffect(() => {
    // bootstrap this with a valid candidate
    if (selectedGeometry === null) {
      onChange(candidates[0]);
    }
  }, [selectedGeometry, candidates, onChange]);

  return (
    <Box display="flex" flex={1}>
      <MapPanel display="flex" flexDirection="column">
        <Box display="flex" justifyContent="center" marginBottom={1}>
          <IconButton
            disabled={currentFeature === 0}
            onClick={() => setCurrentFeature(currentFeature - 1)}
            size="small"
          >
            <LeftArrow />
          </IconButton>
          <Typography variant="subtitle1" sx={{marginX: 1}}>
            {currentFeature + 1} of {nFeatures}
          </Typography>
          <IconButton
            disabled={
              currentFeature === nFeatures - 1 || selectedGeometry === null
            }
            onClick={() => setCurrentFeature(currentFeature + 1)}
            size="small"
          >
            <RightArrow />
          </IconButton>
        </Box>
        <FeaturesMap features={[feature]}>
          <GeojsonLayer
            features={selectedFeatures}
            style={[
              {
                'stroke-width': 5,
                'stroke-color': theme.palette.secondary.light,
              },
              {
                'stroke-width': 3,
                'stroke-color': theme.palette.secondary.main,
              },
            ]}
          />
        </FeaturesMap>
      </MapPanel>
      <Sidebar sx={{overflow: 'hidden', padding: 1.5}}>
        <Typography variant="subtitle2" paragraph>
          Select a simplified alternative geometry
        </Typography>
        <Box height="100%" overflow="auto" paddingRight={1} marginRight={-1}>
          {candidates.map(candidate => (
            <CandidateChooser
              key={candidate.label}
              feature={feature}
              candidate={candidate}
              selected={
                selectedGeometry && selectedGeometry.label
                  ? selectedGeometry.label === candidate.label
                  : false
              }
              onChange={() => {
                onChange(candidate);
              }}
              originalArea={originalArea}
            />
          ))}
        </Box>
      </Sidebar>
    </Box>
  );
};

FeatureCard.propTypes = {
  feature: object,
  selectedGeometry: object,
  onChange: func,
  currentFeature: number,
  setCurrentFeature: func,
  nFeatures: number,
  maxVertexes: number,
};

export default FeatureCard;
